import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { trackEvent } from '../../../lib/analytics';

import { selectIsSidebarWorkspaceMenuEnabled } from '../../../redux/main/main.selectors';
import { selectSidebarCollapsed } from '../../../redux/preferences/preferences.selectors';
import { toggleSidebarWorkspaceMenu, toggleInvitePeople, toggleModalWorkspaceCreate } from '../../../redux/main/main.actions';

import { selectWorkspaceList, selectCurrentWorkspace, selectPendingSwitchId, selectCanInviteWorkspaceMembers } from '../../../redux/workspace/workspace.selectors';
import { workspaceSwitchRequest } from '../../../redux/workspace/workspace.actions';

import { selectAuthUser } from '../../../redux/auth/auth.selectors';

import { useShortKeysNavigation } from '../../../hooks/useShortKeysNavigation';

import {
    ModeDarkIcon,
    ModeLightIcon,
    OkCheckIcon,
} from '../../Icon';

import './ModalWorkspaceMenu.scss';

export const ModalWorkspaceMenu = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { pathname } = useLocation();
    const canSwitchWorkspace = pathname.startsWith('/diagrams');

    const isSidebarCollapsed = useSelector(selectSidebarCollapsed);
    const isOpen = useSelector(selectIsSidebarWorkspaceMenuEnabled);
    const workspaces = useSelector(selectWorkspaceList);
    const currentWorkspace = useSelector(selectCurrentWorkspace);
    const pendingSwitchId = useSelector(selectPendingSwitchId);
    const canManageMembers = useSelector(selectCanInviteWorkspaceMembers);

    const { map: shortKeysMap } = useShortKeysNavigation();

    const currentUser = useSelector(selectAuthUser);

    const [shouldRender, setShouldRender] = useState(isOpen);
    const [animationClass, setAnimationClass] = useState('');

    const ref = useRef(null);
    const justSwitchedToRef = useRef(null);
    const openSubMenuTimerRef = useRef(null);
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
    const dispatch = useDispatch();

    const cancelOpenSubMenuTimer = () => {
        if (openSubMenuTimerRef.current) {
            clearTimeout(openSubMenuTimerRef.current);
            openSubMenuTimerRef.current = null;
        }
    };

    useEffect(() => () => cancelOpenSubMenuTimer(), []);

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setAnimationClass('fade-in-menu');
        } else if (shouldRender) {
            setAnimationClass('fade-out');
        }
    }, [isOpen]);

    const handleAnimationEnd = () => {
        if (!isOpen) {
            setShouldRender(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    const handleCancel = () => {
        dispatch(toggleSidebarWorkspaceMenu({}));
        setIsSubMenuOpen(false);
    };

    useEffect(() => {
        const target = justSwitchedToRef.current;
        if (target && currentWorkspace?.id === target) {
            justSwitchedToRef.current = null;
            dispatch(toggleSidebarWorkspaceMenu({}));
            setIsSubMenuOpen(false);
        }
    }, [currentWorkspace?.id, dispatch]);

    useEffect(() => {
        const handleKeyPress = (event) => {
            if (!isOpen) return;

            if (event.code === 'Escape') {
                event.preventDefault();

                handleCancel();
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [isOpen, handleCancel]);

    if (!shouldRender) return null;

    return (
        <div className="modal-workspace-menu">
            <div className="modal-overlay" onClick={handleOverlayClick}></div>
            <div
                className={`workspace-menu ${animationClass} ${isSidebarCollapsed ? 'collapsed' : ''}`}
                onAnimationEnd={handleAnimationEnd}
                onMouseLeave={() => {
                    // Only cancel a still-pending open — do NOT close an already-open
                    // sub-menu on leave. Once open it stays open until the user hovers
                    // another menu item (Settings / Manage members) or clicks outside.
                    cancelOpenSubMenuTimer();
                }}
            >
                <div className="control">
                    <button
                        className={`control-button`}
                        // className={`control-button control-button-with-key`}
                        // style={{ marginBottom: '-2px' }}
                        type="button"
                        title=""
                        aria-pressed="false"
                        onMouseEnter={() => {
                            cancelOpenSubMenuTimer();
                            setIsSubMenuOpen(false);
                        }}
                        onClick={() => {
                            trackEvent('app_sidebar_workspace_settings', {
                                page: 'app',
                                type: 'sidebar',
                                subtype: 'workspace',
                            });

                            const url = `/settings/preferences/`;
                            // const url = `/settings/workspace/`;
                            dispatch(toggleSidebarWorkspaceMenu({}));
                            setIsSubMenuOpen(false);
                            history.push(url);
                        }}
                    >
                        <svg className="w-menu-icon-settings" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"></path><path d="M18.7273 14.7273C18.6063 15.0015 18.5702 15.3056 18.6236 15.6005C18.6771 15.8954 18.8177 16.1676 19.0273 16.3818L19.0818 16.4364C19.2509 16.6052 19.385 16.8057 19.4765 17.0265C19.568 17.2472 19.6151 17.4838 19.6151 17.7227C19.6151 17.9617 19.568 18.1983 19.4765 18.419C19.385 18.6397 19.2509 18.8402 19.0818 19.0091C18.913 19.1781 18.7124 19.3122 18.4917 19.4037C18.271 19.4952 18.0344 19.5423 17.7955 19.5423C17.5565 19.5423 17.3199 19.4952 17.0992 19.4037C16.8785 19.3122 16.678 19.1781 16.5091 19.0091L16.4545 18.9545C16.2403 18.745 15.9682 18.6044 15.6733 18.5509C15.3784 18.4974 15.0742 18.5335 14.8 18.6545C14.5311 18.7698 14.3018 18.9611 14.1403 19.205C13.9788 19.4489 13.8921 19.7347 13.8909 20.0273V20.1818C13.8909 20.664 13.6994 21.1265 13.3584 21.4675C13.0174 21.8084 12.5549 22 12.0727 22C11.5905 22 11.1281 21.8084 10.7871 21.4675C10.4461 21.1265 10.2545 20.664 10.2545 20.1818V20.1C10.2475 19.7991 10.1501 19.5073 9.97501 19.2625C9.79991 19.0176 9.55521 18.8312 9.27273 18.7273C8.99853 18.6063 8.69437 18.5702 8.39947 18.6236C8.10456 18.6771 7.83244 18.8177 7.61818 19.0273L7.56364 19.0818C7.39478 19.2509 7.19425 19.385 6.97353 19.4765C6.7528 19.568 6.51621 19.6151 6.27727 19.6151C6.03834 19.6151 5.80174 19.568 5.58102 19.4765C5.36029 19.385 5.15977 19.2509 4.99091 19.0818C4.82186 18.913 4.68775 18.7124 4.59626 18.4917C4.50476 18.271 4.45766 18.0344 4.45766 17.7955C4.45766 17.5565 4.50476 17.3199 4.59626 17.0992C4.68775 16.8785 4.82186 16.678 4.99091 16.5091L5.04545 16.4545C5.25503 16.2403 5.39562 15.9682 5.4491 15.6733C5.50257 15.3784 5.46647 15.0742 5.34545 14.8C5.23022 14.5311 5.03887 14.3018 4.79497 14.1403C4.55107 13.9788 4.26526 13.8921 3.97273 13.8909H3.81818C3.33597 13.8909 2.87351 13.6994 2.53253 13.3584C2.19156 13.0174 2 12.5549 2 12.0727C2 11.5905 2.19156 11.1281 2.53253 10.7871C2.87351 10.4461 3.33597 10.2545 3.81818 10.2545H3.9C4.2009 10.2475 4.49273 10.1501 4.73754 9.97501C4.98236 9.79991 5.16883 9.55521 5.27273 9.27273C5.39374 8.99853 5.42984 8.69437 5.37637 8.39947C5.3229 8.10456 5.18231 7.83244 4.97273 7.61818L4.91818 7.56364C4.74913 7.39478 4.61503 7.19425 4.52353 6.97353C4.43203 6.7528 4.38493 6.51621 4.38493 6.27727C4.38493 6.03834 4.43203 5.80174 4.52353 5.58102C4.61503 5.36029 4.74913 5.15977 4.91818 4.99091C5.08704 4.82186 5.28757 4.68775 5.50829 4.59626C5.72901 4.50476 5.96561 4.45766 6.20455 4.45766C6.44348 4.45766 6.68008 4.50476 6.9008 4.59626C7.12152 4.68775 7.32205 4.82186 7.49091 4.99091L7.54545 5.04545C7.75971 5.25503 8.03183 5.39562 8.32674 5.4491C8.62164 5.50257 8.9258 5.46647 9.2 5.34545H9.27273C9.54161 5.23022 9.77093 5.03887 9.93245 4.79497C10.094 4.55107 10.1807 4.26526 10.1818 3.97273V3.81818C10.1818 3.33597 10.3734 2.87351 10.7144 2.53253C11.0553 2.19156 11.5178 2 12 2C12.4822 2 12.9447 2.19156 13.2856 2.53253C13.6266 2.87351 13.8182 3.33597 13.8182 3.81818V3.9C13.8193 4.19253 13.906 4.47834 14.0676 4.72224C14.2291 4.96614 14.4584 5.15749 14.7273 5.27273C15.0015 5.39374 15.3056 5.42984 15.6005 5.37637C15.8954 5.3229 16.1676 5.18231 16.3818 4.97273L16.4364 4.91818C16.6052 4.74913 16.8057 4.61503 17.0265 4.52353C17.2472 4.43203 17.4838 4.38493 17.7227 4.38493C17.9617 4.38493 18.1983 4.43203 18.419 4.52353C18.6397 4.61503 18.8402 4.74913 19.0091 4.91818C19.1781 5.08704 19.3122 5.28757 19.4037 5.50829C19.4952 5.72901 19.5423 5.96561 19.5423 6.20455C19.5423 6.44348 19.4952 6.68008 19.4037 6.9008C19.3122 7.12152 19.1781 7.32205 19.0091 7.49091L18.9545 7.54545C18.745 7.75971 18.6044 8.03183 18.5509 8.32674C18.4974 8.62164 18.5335 8.9258 18.6545 9.2V9.27273C18.7698 9.54161 18.9611 9.77093 19.205 9.93245C19.4489 10.094 19.7347 10.1807 20.0273 10.1818H20.1818C20.664 10.1818 21.1265 10.3734 21.4675 10.7144C21.8084 11.0553 22 11.5178 22 12C22 12.4822 21.8084 12.9447 21.4675 13.2856C21.1265 13.6266 20.664 13.8182 20.1818 13.8182H20.1C19.8075 13.8193 19.5217 13.906 19.2778 14.0676C19.0339 14.2291 18.8425 14.4584 18.7273 14.7273Z"></path></svg>
                        {t('b.settings')}

                        {/* <div className='control-button-with-key-left-part'>
                            <svg className="w-menu-icon-settings" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"></path><path d="M18.7273 14.7273C18.6063 15.0015 18.5702 15.3056 18.6236 15.6005C18.6771 15.8954 18.8177 16.1676 19.0273 16.3818L19.0818 16.4364C19.2509 16.6052 19.385 16.8057 19.4765 17.0265C19.568 17.2472 19.6151 17.4838 19.6151 17.7227C19.6151 17.9617 19.568 18.1983 19.4765 18.419C19.385 18.6397 19.2509 18.8402 19.0818 19.0091C18.913 19.1781 18.7124 19.3122 18.4917 19.4037C18.271 19.4952 18.0344 19.5423 17.7955 19.5423C17.5565 19.5423 17.3199 19.4952 17.0992 19.4037C16.8785 19.3122 16.678 19.1781 16.5091 19.0091L16.4545 18.9545C16.2403 18.745 15.9682 18.6044 15.6733 18.5509C15.3784 18.4974 15.0742 18.5335 14.8 18.6545C14.5311 18.7698 14.3018 18.9611 14.1403 19.205C13.9788 19.4489 13.8921 19.7347 13.8909 20.0273V20.1818C13.8909 20.664 13.6994 21.1265 13.3584 21.4675C13.0174 21.8084 12.5549 22 12.0727 22C11.5905 22 11.1281 21.8084 10.7871 21.4675C10.4461 21.1265 10.2545 20.664 10.2545 20.1818V20.1C10.2475 19.7991 10.1501 19.5073 9.97501 19.2625C9.79991 19.0176 9.55521 18.8312 9.27273 18.7273C8.99853 18.6063 8.69437 18.5702 8.39947 18.6236C8.10456 18.6771 7.83244 18.8177 7.61818 19.0273L7.56364 19.0818C7.39478 19.2509 7.19425 19.385 6.97353 19.4765C6.7528 19.568 6.51621 19.6151 6.27727 19.6151C6.03834 19.6151 5.80174 19.568 5.58102 19.4765C5.36029 19.385 5.15977 19.2509 4.99091 19.0818C4.82186 18.913 4.68775 18.7124 4.59626 18.4917C4.50476 18.271 4.45766 18.0344 4.45766 17.7955C4.45766 17.5565 4.50476 17.3199 4.59626 17.0992C4.68775 16.8785 4.82186 16.678 4.99091 16.5091L5.04545 16.4545C5.25503 16.2403 5.39562 15.9682 5.4491 15.6733C5.50257 15.3784 5.46647 15.0742 5.34545 14.8C5.23022 14.5311 5.03887 14.3018 4.79497 14.1403C4.55107 13.9788 4.26526 13.8921 3.97273 13.8909H3.81818C3.33597 13.8909 2.87351 13.6994 2.53253 13.3584C2.19156 13.0174 2 12.5549 2 12.0727C2 11.5905 2.19156 11.1281 2.53253 10.7871C2.87351 10.4461 3.33597 10.2545 3.81818 10.2545H3.9C4.2009 10.2475 4.49273 10.1501 4.73754 9.97501C4.98236 9.79991 5.16883 9.55521 5.27273 9.27273C5.39374 8.99853 5.42984 8.69437 5.37637 8.39947C5.3229 8.10456 5.18231 7.83244 4.97273 7.61818L4.91818 7.56364C4.74913 7.39478 4.61503 7.19425 4.52353 6.97353C4.43203 6.7528 4.38493 6.51621 4.38493 6.27727C4.38493 6.03834 4.43203 5.80174 4.52353 5.58102C4.61503 5.36029 4.74913 5.15977 4.91818 4.99091C5.08704 4.82186 5.28757 4.68775 5.50829 4.59626C5.72901 4.50476 5.96561 4.45766 6.20455 4.45766C6.44348 4.45766 6.68008 4.50476 6.9008 4.59626C7.12152 4.68775 7.32205 4.82186 7.49091 4.99091L7.54545 5.04545C7.75971 5.25503 8.03183 5.39562 8.32674 5.4491C8.62164 5.50257 8.9258 5.46647 9.2 5.34545H9.27273C9.54161 5.23022 9.77093 5.03887 9.93245 4.79497C10.094 4.55107 10.1807 4.26526 10.1818 3.97273V3.81818C10.1818 3.33597 10.3734 2.87351 10.7144 2.53253C11.0553 2.19156 11.5178 2 12 2C12.4822 2 12.9447 2.19156 13.2856 2.53253C13.6266 2.87351 13.8182 3.33597 13.8182 3.81818V3.9C13.8193 4.19253 13.906 4.47834 14.0676 4.72224C14.2291 4.96614 14.4584 5.15749 14.7273 5.27273C15.0015 5.39374 15.3056 5.42984 15.6005 5.37637C15.8954 5.3229 16.1676 5.18231 16.3818 4.97273L16.4364 4.91818C16.6052 4.74913 16.8057 4.61503 17.0265 4.52353C17.2472 4.43203 17.4838 4.38493 17.7227 4.38493C17.9617 4.38493 18.1983 4.43203 18.419 4.52353C18.6397 4.61503 18.8402 4.74913 19.0091 4.91818C19.1781 5.08704 19.3122 5.28757 19.4037 5.50829C19.4952 5.72901 19.5423 5.96561 19.5423 6.20455C19.5423 6.44348 19.4952 6.68008 19.4037 6.9008C19.3122 7.12152 19.1781 7.32205 19.0091 7.49091L18.9545 7.54545C18.745 7.75971 18.6044 8.03183 18.5509 8.32674C18.4974 8.62164 18.5335 8.9258 18.6545 9.2V9.27273C18.7698 9.54161 18.9611 9.77093 19.205 9.93245C19.4489 10.094 19.7347 10.1807 20.0273 10.1818H20.1818C20.664 10.1818 21.1265 10.3734 21.4675 10.7144C21.8084 11.0553 22 11.5178 22 12C22 12.4822 21.8084 12.9447 21.4675 13.2856C21.1265 13.6266 20.664 13.8182 20.1818 13.8182H20.1C19.8075 13.8193 19.5217 13.906 19.2778 14.0676C19.0339 14.2291 18.8425 14.4584 18.7273 14.7273Z"></path></svg>
                            <span>Settings</span>
                        </div>
                        <div className='control-button-with-key-right-part'>
                            <span className="control-button-with-key-right-part-text">{shortKeysMap?.CTRL}</span>
                            <span className="control-button-with-key-right-part-connector">+</span>
                            <span className="control-button-with-key-right-part-text">,</span>
                        </div> */}
                    </button>
                </div>

                {canManageMembers ?
                    <div className="control">
                        <button
                            className={`control-button ${!canManageMembers ? 'disabled' : ''}`}
                            disabled={!canManageMembers}
                            type="button"
                            title=""
                            aria-pressed="false"
                            onMouseEnter={() => {
                                cancelOpenSubMenuTimer();
                                setIsSubMenuOpen(false);
                            }}
                            onClick={() => {
                                trackEvent('app_sidebar_invite_members', {
                                    page: 'app',
                                    type: 'sidebar',
                                    subtype: 'workspace',
                                });

                                if (canManageMembers) {
                                    const url = `/settings/members/`;
                                    dispatch(toggleSidebarWorkspaceMenu({}));
                                    setIsSubMenuOpen(false);
                                    history.push(url);
                                }

                                // dispatch(toggleInvitePeople({}));
                                // setTimeout(() => {
                                //     // setIsSubMenuOpen(false);
                                //     handleCancel();
                                // }, 20);

                            }}
                        >
                            {/* <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15.5H7.5C6.10444 15.5 5.40665 15.5 4.83886 15.6722C3.56045 16.06 2.56004 17.0605 2.17224 18.3389C2 18.9067 2 19.6044 2 21M19 21V15M16 18H22M14.5 7.5C14.5 9.98528 12.4853 12 10 12C7.51472 12 5.5 9.98528 5.5 7.5C5.5 5.01472 7.51472 3 10 3C12.4853 3 14.5 5.01472 14.5 7.5Z"></path></svg> */}
                            <svg className="w-menu-icon-members" width="14px" height="14px" viewBox="0 0 24 24" fill="currentColor" stroke="none"><g><path fillRule="evenodd" clipRule="evenodd" d="M9 0C5.96243 0 3.5 2.46243 3.5 5.5C3.5 8.53757 5.96243 11 9 11C12.0376 11 14.5 8.53757 14.5 5.5C14.5 2.46243 12.0376 0 9 0ZM5.5 5.5C5.5 3.567 7.067 2 9 2C10.933 2 12.5 3.567 12.5 5.5C12.5 7.433 10.933 9 9 9C7.067 9 5.5 7.433 5.5 5.5Z" fill="currentColor" stroke="none"></path> <path d="M15.5 0C14.9477 0 14.5 0.447715 14.5 1C14.5 1.55228 14.9477 2 15.5 2C17.433 2 19 3.567 19 5.5C19 7.433 17.433 9 15.5 9C14.9477 9 14.5 9.44771 14.5 10C14.5 10.5523 14.9477 11 15.5 11C18.5376 11 21 8.53757 21 5.5C21 2.46243 18.5376 0 15.5 0Z" fill="currentColor" stroke="none"></path> <path d="M19.0837 14.0157C19.3048 13.5096 19.8943 13.2786 20.4004 13.4997C22.5174 14.4246 24 16.538 24 19V21C24 21.5523 23.5523 22 23 22C22.4477 22 22 21.5523 22 21V19C22 17.3613 21.0145 15.9505 19.5996 15.3324C19.0935 15.1113 18.8625 14.5217 19.0837 14.0157Z" fill="currentColor" stroke="none"></path> <path d="M6 13C2.68629 13 0 15.6863 0 19V21C0 21.5523 0.447715 22 1 22C1.55228 22 2 21.5523 2 21V19C2 16.7909 3.79086 15 6 15H12C14.2091 15 16 16.7909 16 19V21C16 21.5523 16.4477 22 17 22C17.5523 22 18 21.5523 18 21V19C18 15.6863 15.3137 13 12 13H6Z" fill="currentColor" stroke="none"></path></g></svg>
                            {/* Invite members */}
                            {t('m_workspace_menu.manage_members')}
                        </button>
                    </div>
                    : ''
                }

                {canSwitchWorkspace && (
                    <div className="horizontal-line"></div>
                )}

                {canSwitchWorkspace && (
                    <div className="control">
                        <button
                            ref={ref}
                            className={`control-button btn-space-between ${isSubMenuOpen ? 'selected' : ''}`}

                            type="button"
                            title=""
                            aria-pressed="false"
                            onMouseEnter={() => {
                                if (isSubMenuOpen) return;
                                cancelOpenSubMenuTimer();
                                openSubMenuTimerRef.current = setTimeout(() => {
                                    openSubMenuTimerRef.current = null;
                                    trackEvent('app_sidebar_workspace_switch_menu', {
                                        page: 'app',
                                        type: 'sidebar',
                                        subtype: 'workspace',
                                    });
                                    setIsSubMenuOpen(true);
                                }, 150);
                            }}
                            onMouseLeave={cancelOpenSubMenuTimer}
                        >
                            <div className='btn-left-part'>
                                <svg className="w-menu-icon-switch" width="20px" height="24px" viewBox="0 -0.5 24 24" fill="none"><g> <path stroke="#d7dee8" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 6H9a6 6 0 1 0 0 12h6a6 6 0 0 0 0-12Z"></path> <circle cx="15" cy="12" r="3" fill="#d7dee8" stroke="#d7dee8" strokeWidth="2"></circle> </g></svg>
                                {t('m_workspace_menu.switch_workspace')}
                            </div>
                            <svg className='svg-arrow-right' width="18px" height="18px" fill="none" viewBox="0 -2 24 24" stroke="#8e94afb3" strokeWidth="2.6px" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" stroke="rgb(134, 145, 148)"></path></svg>
                        </button>
                    </div>
                )}

            </div>

            {canSwitchWorkspace && isSubMenuOpen && (
                <div
                    className={`workspace-sub-menu ${isSidebarCollapsed ? 'collapsed' : ''} ${!canManageMembers ? 'smaller-menu' : ''}`}
                >
                    <div className="workspace-sub-menu-label">{t('m_workspace_menu.workspaces_label')}</div>
                    {/* <div className="workspace-sub-menu-label">{currentUser?.email || currentUser?.name}</div> */}

                    <div className="workspace-sub-menu-list">
                        {workspaces.map((ws) => {
                            const isActive = currentWorkspace?.id === ws.id;
                            const isPending = pendingSwitchId === ws.id;
                            return (
                                <div className="control" key={ws.id}>
                                    <button
                                        className={`control-button ${isActive ? 'selected' : ''}`}
                                        type="button"
                                        disabled={!!pendingSwitchId}
                                        onClick={() => {
                                            if (isActive || isPending) return;
                                            trackEvent('app_sidebar_workspace_switch', {
                                                page: 'app',
                                                type: 'sidebar',
                                                subtype: 'workspace',
                                                workspace_id: ws.id,
                                            });
                                            justSwitchedToRef.current = ws.id;
                                            dispatch(workspaceSwitchRequest(ws.id));
                                        }}
                                    >
                                        <span className="workspace-icon">
                                            {ws.title?.charAt(0)?.toUpperCase()}
                                        </span>
                                        <span className="workspace-name">{ws.title}</span>
                                        {isActive && !isPending && <OkCheckIcon />}
                                        {isPending && <span className="workspace-switch-spinner" aria-label={t('m_workspace_menu.switching')} />}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <div className="horizontal-line"></div>

                    <div className="control">
                        <button
                            className="control-button create-workspace"
                            type="button"
                            onClick={() => {
                                trackEvent('app_sidebar_workspace_create', {
                                    page: 'app',
                                    type: 'sidebar',
                                    subtype: 'workspace',
                                });
                                // setTimeout(() => {
                                dispatch(toggleSidebarWorkspaceMenu({}));
                                setIsSubMenuOpen(false);
                                dispatch(toggleModalWorkspaceCreate());
                                // }, 20);
                            }}
                        >
                            {/* <span className="workspace-icon add">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"></path></svg>
                            </span> */}
                            <span className="workspace-name create">{t('m_workspace_menu.create_workspace')}</span>
                        </button>
                    </div>

                    {/* <div className="control">
                        <button
                            className="control-button create-workspace join-workspace"
                            type="button"
                            onClick={() => {
                                trackEvent('app_sidebar_workspace_create', {
                                    page: 'app',
                                    type: 'sidebar',
                                    subtype: 'workspace',
                                });
                                // setTimeout(() => {
                                dispatch(toggleSidebarWorkspaceMenu({}));
                                setIsSubMenuOpen(false);
                                dispatch(toggleModalWorkspaceCreate());
                                // }, 20);
                            }}
                        > */}
                    {/* <div className="workspace-name create">Join a workspace... <div className="join-workspace-tag">0 available</div></div> */}
                    {/* <div className="workspace-name create">Join a workspace... </div>
                        </button>
                    </div> */}

                </div>
            )}
        </div>
    );
};

export default ModalWorkspaceMenu;
